/**
 * TcmTenantSelector — shared tenant-switcher for the lab.
 *
 * Wraps the existing `useTenants` hook (the same one driving the Admin
 * Portal tenants tab). The lab is Platform-Admin-only, so we surface ALL
 * monitored tenants regardless of role — Platform Admins have universal
 * tenant access by definition.
 *
 * Controlled component: parent owns the selected `tenantId` so the value
 * can be lifted into URL state (the lab page persists `?tenant=...`).
 *
 * Carbon: the field-framed native select (keyboard handling and
 * screen-reader semantics for free, no dropdown library). Voice: the lab is
 * diagnostic, so options read name + short tenant ID with no verdict
 * framing; the empty state names the reason, not a count.
 */

import { useMemo } from 'react'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Select } from '@/components/carbon/Select'
import { useTenants } from '@/hooks/useTenants'

interface TcmTenantSelectorProps {
  value: string | null
  onChange: (tenantId: string) => void
  className?: string
}

export default function TcmTenantSelector({
  value,
  onChange,
  className,
}: TcmTenantSelectorProps) {
  const { tenants, isLoading, isError } = useTenants()

  // Sort once. The Admin Portal sorts client-side too, but `useTenants`
  // returns raw rows — we sort by display name so the lab dropdown matches
  // the rest of Guardian's tenant lists.
  const sortedTenants = useMemo(
    () => [...tenants].filter(t => t.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [tenants],
  )

  if (isLoading) {
    return (
      <Select
        label="Tenant"
        aria-label="Select tenant"
        value=""
        disabled
        onChange={() => undefined}
        options={[{ value: '', label: 'Loading tenants' }]}
        wrapperClassName={className}
      />
    )
  }

  if (isError) {
    return (
      <InlineNotification kind="error" title="Tenant list unavailable." className={className}>
        Reload the page to try again.
      </InlineNotification>
    )
  }

  if (sortedTenants.length === 0) {
    return <p className={`m-0 text-body text-text-secondary ${className ?? ''}`}>No tenants available.</p>
  }

  return (
    <Select
      label="Tenant"
      aria-label="Select tenant"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      wrapperClassName={className}
    >
      {value === null && (
        <option value="" disabled>
          Pick a tenant
        </option>
      )}
      {sortedTenants.map(tenant => (
        <option key={tenant.tenantId} value={tenant.tenantId}>
          {tenant.name} · {tenant.tenantId.slice(0, 8)}
        </option>
      ))}
    </Select>
  )
}
